/**
 * Build-time environment helpers.
 *
 * The bundled web app gets a ``VITE_ENV`` build-time string (one of
 * ``dev`` / ``qa`` / ``stage`` / ``prod``). The CI workflow that
 * builds for each environment sets it; in local dev it falls back to
 * ``dev`` so a forgotten env var doesn't accidentally enable a prod
 * codepath.
 *
 * The reason this exists: user-facing diagnostic messages should be
 * verbose pre-prod (helps the operator self-serve) but generic in
 * prod (don't leak URL conventions, internal shape, or
 * implementation details to whoever lands on a page they shouldn't).
 * Same idea for any other "should I tell the truth or be vague?"
 * decision.
 */

const ENV = (import.meta.env.VITE_ENV ?? "dev").trim().toLowerCase();

const PREPROD_VALUES = new Set(["dev", "qa", "stage", "staging", "test"]);

/** True for dev / qa / stage / staging / test bundles. False for
 *  prod. Default-on for safety: a build with no VITE_ENV set is
 *  treated as pre-prod so a forgotten variable can't accidentally
 *  hide debugging info from an operator we trust. The build pipeline
 *  always sets VITE_ENV explicitly for prod, so the production
 *  bundle is always recognised. */
export function isPreProd(): boolean {
  return PREPROD_VALUES.has(ENV) || !ENV;
}

/** Strict opposite - only true when VITE_ENV is set to ``prod``. */
export function isProd(): boolean {
  return ENV === "prod" || ENV === "production";
}

/** Raw value, lowercased. Useful when callers need to surface the
 *  env tag in a debug-only badge. */
export function envTag(): string {
  return ENV;
}
